// @ts-nocheck
/**
 * SMS Webhooks list — redesigned to obd3-gui pattern (FillPage + PageHeader
 * + row-action DropdownMenu + ConfirmDialog + JSON viewer dialog).
 * Backend contract preserved: API_SMSWEBHOOK_LIST / API_SMSWEBHOOK_DELETE.
 */
import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { Eye, Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import CopyButton from './components/CopyButton';
import Loader from '@/components/partials/components/Loader';
import { getApiCall, postApiCall } from '@/service/httpCall';
import { API_SMSWEBHOOK_LIST, API_SMSWEBHOOK_DELETE } from '@/service/url';
import { useAuth, errorCheck } from '@/views/dashboard/auth/auth-utils';
import { ROUTES, MODULE_ACL, USER_ROLES } from '@/utilities/constant';
import { exportToCSV, hasPermission } from '@/utilities/helpers';
import { getUser } from '@/utilities/setting';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';

interface JsonModal {
  title: string;
  content: string;
  isJson: boolean;
}

const SmsWebhookList = () => {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();

  const [showToast, setShowToast] = useState(false);
  const [toastProps, setToastProps] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [webhooksData, setWebhooksData] = useState<any[]>([]);
  const [pendingDelete, setPendingDelete] = useState<any>(null);
  const [jsonModal, setJsonModal] = useState<JsonModal | null>(null);
  const [loggedInUserACL, setLoggedInUserACL] = useState<any>({});
  const [isSuperAdmin, setIsSuperadmin] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [moduleOperationsForReadPermissionCheck, setModuleOperationsForReadPermissionCheck] =
    useState<any>({});

  useEffect(() => {
    setLoading(true);
    const user = getUser();
    if (user?.role === USER_ROLES.SUPERADMIN) setIsSuperadmin(true);
    if (user?.acl_settings) {
      const parsed = parseModuleOperations(user.acl_settings.module_operations);
      setLoggedInUserACL(parsed);
      setModuleOperationsForReadPermissionCheck(parsed);
    }
    fetchList();
  }, []);

  useEffect(() => {
    const onAclRefreshed = () => {
      const user = getUser();
      if (user?.acl_settings?.module_operations) {
        try {
          const parsed =
            typeof user.acl_settings.module_operations === 'string'
              ? parseModuleOperations(user.acl_settings.module_operations)
              : user.acl_settings.module_operations;
          setLoggedInUserACL(parsed);
          setModuleOperationsForReadPermissionCheck(parsed);
        } catch (e) {
          console.log('Failed to apply refreshed ACL', e);
        }
      }
    };
    window.addEventListener('acl-refreshed', onAclRefreshed);
    return () => window.removeEventListener('acl-refreshed', onAclRefreshed);
  }, []);

  const fetchList = () => {
    getApiCall(API_SMSWEBHOOK_LIST, {})
      .then((resp: any) => setWebhooksData(resp ?? []))
      .catch((err: any) => {
        if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
          errorCheck(err, navigate, setToastProps, setShowToast);
          toast.error(friendlyApiError(err) || 'Failed to load webhooks');
        } else {
          console.log(err);
        }
      })
      .finally(() => setLoading(false));
  };

  const confirmDelete = () => {
    if (!pendingDelete) return;
    setDeleting(true);
    postApiCall(API_SMSWEBHOOK_DELETE, { id: pendingDelete.id })
      .then(() => {
        toast.success('Webhook deleted successfully');
        setPendingDelete(null);
        fetchList();
      })
      .catch((err: any) => toast.error(friendlyApiError(err) || 'Failed to delete webhook'))
      .finally(() => setDeleting(false));
  };

  const openJson = (content: any, title: string, isJson = false) => {
    const contentStr = typeof content === 'string' ? content : JSON.stringify(content);
    setJsonModal({ title, content: contentStr ?? '', isJson });
  };

  const formatJson = (jsonString: string) => {
    try {
      return JSON.stringify(JSON.parse(jsonString), null, 2);
    } catch {
      return jsonString;
    }
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      exportToCSV({
        fileName: 'sms-webhooks',
        data: webhooksData,
        columns: [
          { header: 'Webhook Id', accessor: 'id' },
          { header: 'Webhook Name', accessor: (row: any) => row.webhook_name || '-' },
          { header: 'SMS Text', accessor: (row: any) => row.sms_text || '-' },
          {
            header: 'Payload',
            accessor: (row: any) =>
              row.payload
                ? typeof row.payload === 'string'
                  ? row.payload
                  : JSON.stringify(row.payload)
                : '-',
          },
          {
            header: 'Headers',
            accessor: (row: any) =>
              row.header
                ? typeof row.header === 'string'
                  ? row.header
                  : JSON.stringify(row.header)
                : '-',
          },
          { header: 'Webhook URL', accessor: (row: any) => row.url || '-' },
          {
            header: 'Method',
            accessor: (row: any) =>
              row.request_type === 'WHATSAPP' ? 'POST' : row.request_type || '-',
          },
          {
            header: 'Created',
            accessor: (row: any) =>
              row.created_at ? new Date(row.created_at).toLocaleString('en-IN') : '-',
          },
        ],
      });
    } finally {
      setExporting(false);
    }
  };

  const isCallCentreAdmin = getUser()?.role === USER_ROLES.CALL_CENTRE_ADMIN;
  const canRead = hasPermission(
    moduleOperationsForReadPermissionCheck,
    MODULE_ACL.SMS_WEBHOOK,
    'read',
  );
  const canAdd =
    isCallCentreAdmin &&
    hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.SMS_WEBHOOK, 'add');
  const canEdit =
    isCallCentreAdmin &&
    hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.SMS_WEBHOOK, 'edit');
  const canDelete =
    isCallCentreAdmin &&
    hasPermission(moduleOperationsForReadPermissionCheck, MODULE_ACL.SMS_WEBHOOK, 'delete');
  const showActions = canEdit || canDelete;

  const columns = useMemo<ColumnDef<any>[]>(() => {
    const cols: ColumnDef<any>[] = [
      {
        accessorKey: 'id',
        header: 'ID',
        size: 80,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">{row.original.id}</span>
        ),
      },
      {
        accessorKey: 'webhook_name',
        header: 'Name',
        cell: ({ row }) =>
          canEdit ? (
            <Link
              to={`/${ROUTES.SMSWEBHOOK.UPDATE.replace(':id', row.original.id)}`}
              className="font-medium text-foreground hover:underline"
            >
              {row.original.webhook_name}
            </Link>
          ) : (
            <span className="font-medium text-foreground">{row.original.webhook_name}</span>
          ),
      },
      {
        accessorKey: 'url',
        header: 'URL',
        size: 100,
        enableSorting: false,
        cell: ({ row }) =>
          row.original.url ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => openJson(row.original.url, 'URL', false)}
            >
              <Eye className="h-4 w-4" />
              View
            </Button>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
      {
        accessorKey: 'request_type',
        header: 'Method',
        size: 100,
        cell: ({ row }) => {
          const t = row.original.request_type === 'WHATSAPP' ? 'POST' : row.original.request_type;
          return (
            <span className="inline-flex rounded-md bg-muted px-2 py-0.5 text-xs font-medium">
              {t || '—'}
            </span>
          );
        },
      },
      {
        accessorKey: 'sms_text',
        header: 'SMS Text',
        cell: ({ row }) =>
          row.original.sms_text ? (
            <span className="line-clamp-2 text-xs">{row.original.sms_text}</span>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
      {
        id: 'payload',
        header: 'Payload',
        size: 100,
        enableSorting: false,
        cell: ({ row }) =>
          row.original.payload ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => openJson(row.original.payload, 'Payload', true)}
            >
              <Eye className="h-4 w-4" />
              View
            </Button>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
      {
        id: 'header',
        header: 'Headers',
        size: 100,
        enableSorting: false,
        cell: ({ row }) =>
          row.original.header ? (
            <Button
              size="sm"
              variant="outline"
              onClick={() => openJson(row.original.header, 'Headers', true)}
            >
              <Eye className="h-4 w-4" />
              View
            </Button>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
      {
        accessorKey: 'created_at',
        header: 'Created',
        cell: ({ row }) =>
          row.original.created_at ? (
            <span className="text-xs text-muted-foreground">
              {new Date(row.original.created_at).toLocaleString()}
            </span>
          ) : (
            '—'
          ),
      },
    ];

    if (showActions) {
      cols.push({
        id: 'actions',
        header: 'Action',
        size: 90,
        enableHiding: false,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex items-center gap-1.5">
            {canEdit && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                aria-label="Edit webhook"
                onClick={() =>
                  navigate(`/${ROUTES.SMSWEBHOOK.UPDATE.replace(':id', row.original.id)}`)
                }
              >
                <Pencil className="h-4 w-4" />
              </Button>
            )}
            {canDelete && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-destructive hover:text-destructive"
                aria-label="Delete webhook"
                onClick={() => setPendingDelete(row.original)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        ),
      });
    }

    return cols;
  }, [showActions, canEdit, canDelete, isSuperAdmin, loggedInUserACL, navigate]);

  if (!canRead) {
    return (
      <FillPage>
        <PageHeader title="SMS" />
        <div className="rounded-md border border-border bg-muted/30 p-8 text-center text-sm text-muted-foreground">
          No read permission found in assigned ACL.
        </div>
      </FillPage>
    );
  }

  if (loading) return <Loader />;

  return (
    <FillPage>
      <PageHeader
        title="SMS Webhook"
        description="Outbound HTTP endpoints called on SMS delivery events. Test before saving."
        actions={
          <>
            {webhooksData.length > 0 && (
              <Button variant="outline" onClick={handleExport} disabled={exporting}>
                Export
              </Button>
            )}
            {canAdd && (
              <Button asChild>
                <Link to={`/${ROUTES.SMSWEBHOOK.CREATE}`}>
                  <Plus className="h-4 w-4" />
                  Add SMS Webhook
                </Link>
              </Button>
            )}
          </>
        }
      />

      <DataTable
        tableId="sms-webhooks:list:v1"
        columns={columns}
        data={webhooksData}
        isLoading={loading}
        searchPlaceholder="Search webhooks by name…"
        emptyTitle="No SMS webhooks yet"
        emptyDescription="Add a webhook to start receiving HTTP callbacks for SMS events."
        getRowId={(row: any) => String(row.id)}
        initialColumnPinning={showActions ? { right: ['actions'] } : undefined}
        exportFilename="sms-webhooks.csv"
        pageSize={10}
        disableExport
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Delete this webhook?"
        description={
          <>
            Webhook{' '}
            <span className="font-medium text-foreground">
              {pendingDelete?.webhook_name}
            </span>{' '}
            will be permanently deleted. Any campaigns firing this webhook will stop delivering.
          </>
        }
        confirmLabel="Delete"
        tone="destructive"
        loading={deleting}
        onConfirm={confirmDelete}
      />

      <Dialog open={jsonModal !== null} onOpenChange={(o) => !o && setJsonModal(null)}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{jsonModal?.title}</DialogTitle>
          </DialogHeader>
          <div className="flex justify-end">
            <CopyButton text={jsonModal?.content ?? ''} />
          </div>
          <div
            className="overflow-auto rounded-md border border-border bg-muted/30 p-3 font-mono text-xs whitespace-pre-wrap break-words"
            style={{ maxHeight: '60vh' }}
          >
            {jsonModal?.isJson ? formatJson(jsonModal.content) : jsonModal?.content}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setJsonModal(null)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </FillPage>
  );
};

export default SmsWebhookList;
